import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories, SHIPPING_TIME, FREE_SHIPPING_THRESHOLD } from '../data/mock';
import { ShoppingCart, ChevronRight, Minus, Plus, Truck, Shield, RotateCcw, CheckCircle, Zap, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  
  const product = products.find(p => p.slug === slug);
  const category = categories.find(c => c.id === product?.categoryId);
  const relatedProducts = products.filter(p => p.categoryId === product?.categoryId && p.id !== product?.id).slice(0, 3);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | Keto Vybe`;
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="pt-28 flex items-center justify-center py-20">
          <div className="text-center">
            <h1 className="text-2xl font-display text-white mb-4">Product Not Found</h1>
            <Link to="/shop" className="text-vybe-400 hover:text-vybe-300 font-medium">
              ← Back to Shop
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-slate-400 hover:text-vybe-400 transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 text-slate-600" />
              <Link to="/shop" className="text-slate-400 hover:text-vybe-400 transition-colors">Shop</Link>
              <ChevronRight className="w-4 h-4 text-slate-600" />
              <Link to={`/shop?category=${category?.slug}`} className="text-slate-400 hover:text-vybe-400 transition-colors">
                {category?.name}
              </Link>
              <ChevronRight className="w-4 h-4 text-slate-600" />
              <span className="text-white font-medium truncate">{product.name}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Product Image */}
            <div className="space-y-4">
              <div className="relative bg-slate-800/50 rounded-3xl border border-slate-700/50 overflow-hidden">
                <div className="aspect-square">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {product.badge && (
                  <span className="absolute top-6 left-6 px-4 py-2 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white text-sm font-bold rounded-full shadow-lg">
                    {product.badge}
                  </span>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-vybe-600/20 rounded-full text-vybe-400 text-sm font-medium mb-4">
                  <Zap className="w-4 h-4" />
                  {category?.name}
                </div>
                <h1 className="font-display text-2xl lg:text-3xl text-white mb-2">
                  {product.name}
                </h1>
                <p className="text-slate-400">{product.description}</p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">${product.price.toFixed(2)}</span>
              </div>

              {/* Features */}
              {product.features && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-white mb-3">Key Benefits</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {product.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-vybe-400 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <div className="flex items-center bg-slate-800 rounded-xl border border-slate-700">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 text-slate-400 hover:text-white transition-colors"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="w-12 text-center text-white font-semibold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 text-slate-400 hover:text-white transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  className="flex-1 flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 hover:from-vybe-500 hover:to-vybe-400 text-white font-bold rounded-xl transition-all shadow-button"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 py-6 border-t border-b border-slate-800">
                {[
                  { icon: Truck, title: 'Free Shipping', desc: `Over $${FREE_SHIPPING_THRESHOLD}` },
                  { icon: Shield, title: 'Secure', desc: 'Checkout' },
                  { icon: RotateCcw, title: '30-Day', desc: 'Returns' },
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <item.icon className="w-5 h-5 text-vybe-400 mx-auto mb-1" />
                    <p className="text-white text-xs font-semibold">{item.title}</p>
                    <p className="text-slate-500 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Shipping Info */}
              <div className="mt-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-cyan-400" />
                  <div>
                    <p className="text-white text-sm font-medium">Fast Delivery</p>
                    <p className="text-slate-400 text-xs">Ships in {SHIPPING_TIME} via USPS</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-20">
              <h2 className="font-display text-2xl text-white mb-8">Related Products</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedProducts.map((item) => (
                  <Link
                    key={item.id}
                    to={`/product/${item.slug}`}
                    className="group bg-slate-800/50 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-vybe-500/50 transition-all"
                  >
                    <div className="aspect-square bg-slate-800 relative overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-white text-sm mb-2 group-hover:text-vybe-400 transition-colors line-clamp-2">
                        {item.name}
                      </h3>
                      <span className="text-lg font-bold text-white">${item.price.toFixed(2)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
